import { useCallback } from "react";
import ReduxFetchState from "redux-fetch-state";
import { put, takeEvery, select } from "redux-saga/effects";
import axios from "../../../../Utils/axiosConfig";
import { getCurrentLanguage } from "../../../../Utils/sagaHelpers";
import { useAppDispatch, useAppSelector } from "../../../../Hooks/useStore";
import { alert } from "../../../../Utils/toast";
import { ChoosePurchaseModelDataTypes } from "../../../../Schema/choosePurchaseModel.types";

export type ChoosePurchaseModelLoadAction = {
  type: string;
  payload: {
    model_id: number;
    user_service_id: number;
    discount_code?: string;
  };
};

export type ChoosePurchaseModelData = {
  data: ChoosePurchaseModelDataTypes;
};

const ChoosePurchaseModel = new ReduxFetchState<
  ChoosePurchaseModelData,
  { model_id: number; user_service_id: number; discount_code?: string },
  any
>("choosePurchaseModel");

export function* watchChoosePurchaseModel(action: ChoosePurchaseModelLoadAction) {
  try {
    const { model_id, user_service_id, discount_code } = action.payload;

    const formData = new FormData();

    if (model_id) {
      formData.append("model_id", String(model_id));
    }
    if (user_service_id) {
      formData.append("user_service_id", String(user_service_id));
    }

    const lang: "fa" | "en" = yield getCurrentLanguage();

    formData.append("callback_url", `${window.location.origin}/${lang}/result/${user_service_id}`);

    const data: ChoosePurchaseModelData = yield axios({
      headers: {
        "Content-Type": "multipart/form-data",
        lang,
      },
      url: `/api/v1/ai/purchase/choose-model${discount_code ? "?discount_code=" + discount_code : ""}`,
      method: "POST",
      data: formData,
    });

    if (data.data.redirect_link) {
      window.open(data.data.redirect_link, "_self");
    } else {
      window.location.href = `/result/${user_service_id}`;
    }

    yield put(ChoosePurchaseModel.actions.loadSuccess({ data: data.data }));
  } catch (e: any) {
    alert({
      message: e?.response?.data?.error ?? "مشکلی پیش آمده",
      type: "error",
    });
    yield put(ChoosePurchaseModel.actions.loadFailure(e.message));
  }
}

export function* choosePurchaseModelSagas() {
  yield takeEvery(ChoosePurchaseModel.actionTypes.load, watchChoosePurchaseModel);
}

export function useChoosePurchaseModel() {
  const { loading, data, ...choosePurchaseModelState } = useAppSelector(
    (state) => state.choosePurchaseModel,
  );
  const dispatch = useAppDispatch();

  const dispatchChoosePurchaseModel = useCallback(
    (data: { model_id: number; user_service_id: number; discount_code?: string }) => {
      dispatch(ChoosePurchaseModel.actions.load(data));
    },
    [dispatch],
  );

  return {
    ...choosePurchaseModelState,
    dispatchChoosePurchaseModel,
    choosePurchaseModelLoading: loading,
    choosePurchaseModelData: data,
  };
}

export const {
  actionTypes: choosePurchaseModelActionsTypes,
  actions: choosePurchaseModelActions,
  reducer: choosePurchaseModelReducer,
} = ChoosePurchaseModel;
